import React from "react";
import { CloseCircle, Danger, InfoCircle, TickCircle } from "iconsax-react";
import styled from "styled-components";

import { Md3Button } from "../Md3Button";
import { ModalAlertPanel, ModalShell } from "../ModalShell";
import { md3CardRadius } from "../../styles/md3Components";

export type ValidationModalType = "success" | "error" | "warning" | "info";

export type ValidationModalAction = {
  label: string;
  onPress: () => void;
};

export type ValidationModalProps = {
  open: boolean;
  onClose: () => void;
  type: ValidationModalType;
  title: string;
  message: string;
  confirmLabel?: string;
  actions?: ValidationModalAction[];
};

const Body = styled(ModalAlertPanel)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
`;

const IconWrap = styled.div<{ $color: string; $large?: boolean }>`
  width: ${({ $large }) => ($large ? 80 : 64)}px;
  height: ${({ $large }) => ($large ? 80 : 64)}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: color-mix(in srgb, ${({ $color }) => $color} 14%, transparent);
  color: ${({ $color }) => $color};
  box-shadow: ${({ $large, $color }) =>
    $large
      ? `0 8px 24px color-mix(in srgb, ${$color} 35%, transparent)`
      : "none"};
`;

const Title = styled.h2<{ $tone?: string }>`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ $tone, theme }) => $tone ?? theme.colors.textTitle};
  line-height: 1.3;
`;

const Message = styled.p`
  margin: 0 0 24px;
  font-size: 19px;
  line-height: 32px;
  color: var(--color-text-secondary);
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

function typeIcon(type: ValidationModalType) {
  switch (type) {
    case "success":
      return TickCircle;
    case "error":
      return CloseCircle;
    case "warning":
      return Danger;
    default:
      return InfoCircle;
  }
}

function typeColor(type: ValidationModalType): string {
  switch (type) {
    case "success":
      return "var(--color-success)";
    case "error":
      return "var(--color-danger)";
    case "warning":
      return "var(--color-warning)";
    default:
      return "var(--color-info)";
  }
}

function defaultLabel(type: ValidationModalType): string {
  if (type === "success") return "Ótimo!";
  return "OK";
}

function titleTone(type: ValidationModalType): string | undefined {
  if (type === "error") return "var(--color-danger)";
  if (type === "warning") return "var(--color-warning)";
  if (type === "success") return "var(--color-success)";
  return undefined;
}

export function ValidationModal({
  open,
  onClose,
  type,
  title,
  message,
  confirmLabel,
  actions = [],
}: ValidationModalProps) {
  const color = typeColor(type);
  const Icon = typeIcon(type);
  const hasCustomActions = actions.length > 0;

  return (
    <ModalShell open={open} onClose={onClose} maxWidth={520} aria-label={title}>
      <Body>
        <IconWrap $color={color} $large={type === "success"}>
          <Icon size={type === "success" ? 36 : 32} variant="Bold" />
        </IconWrap>

        <Title $tone={titleTone(type)}>{title}</Title>
        <Message>{message}</Message>

        <Actions>
          {hasCustomActions ? (
            actions.map((action) => (
              <Md3Button
                key={action.label}
                title={action.label}
                size="large"
                fullWidth
                variant="filled"
                onClick={action.onPress}
              />
            ))
          ) : (
            <Md3Button
              title={confirmLabel ?? defaultLabel(type)}
              size="large"
              fullWidth
              variant="filled"
              color={type === "error" ? "danger" : "primary"}
              onClick={onClose}
            />
          )}
        </Actions>
      </Body>
    </ModalShell>
  );
}
